import {
  ColumnDef, flexRender, getCoreRowModel,
  useReactTable, SortingState, getPaginationRowModel,
  getSortedRowModel, VisibilityState, Column,
  Row,
  ColumnPinningState,
} from "@tanstack/react-table";
import {
  Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CSSProperties, JSX, useState } from "react";
import { ValueSkeleton } from "@/components/skeletons";
import { Button } from "@/components/ui/button";
import { ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  loading: boolean;
  displayAmount: number;
  disableRowColour?: boolean;
  headerStyling?: string;
  defaultSortBy?: { id: string, desc: boolean }
  clickableRows?: boolean;
  onRowClick?: (_row: TData) => void;
  selectableRows?: boolean;
  preselectedRowId?: string;
  disableSorting?: boolean;
  enableColumnPinning?: boolean;
  columnPinning?: ColumnPinningState;
  className?: string;
}

export interface TableColumn<TData, TValue> extends Omit<ColumnDef<TData, TValue>, "cell" | "header"> {
  header?: any;
  cell?: any;
  accessorKey?: string;
}

export function DataTable<TData, TValue>(props: DataTableProps<TData, TValue>) {
  const {
    columns,
    data,
    loading,
    displayAmount = 10,
    disableRowColour,
    headerStyling,
    defaultSortBy,
    clickableRows,
    onRowClick,
    selectableRows,
    preselectedRowId,
    disableSorting = false,
    enableColumnPinning = false,
    columnPinning = { left: [], right: [] },
    className,
  } = props;

  const { t } = useTranslation(["common"]);

  const [sorting, setSorting] = useState<SortingState>(
    defaultSortBy && !disableSorting ? [{ id: defaultSortBy.id, desc: defaultSortBy.desc }] : []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [selectedRowId, setSelectedRowId] = useState<any | null>(preselectedRowId ?? null);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: disableSorting ? undefined : setSorting,
    getSortedRowModel: disableSorting ? undefined : getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnPinning,
    state: {
      sorting: disableSorting ? [] : sorting,
      columnVisibility,
      columnPinning,
    },
    enableSorting: !disableSorting,
  });

  const handleRowClick = (row: Row<TData>) => {
    if (onRowClick) {
      onRowClick(row.original);
    }

    if (selectableRows) {
      setSelectedRowId(row.id);
    }
  };

  /**
   * Function to get the styles for columns with pinning
   * @param column - The column to get the styles for
   * @param isHeader - Whether the column is a header column, apply sticky and higher z index
   * @returns The styles for the column
   */
  const getCommonPinningStyles = (column: Column<TData>, isHeader = false): CSSProperties => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-2px 0 2px -2px rgb(203 213 225) inset"
        : isFirstRightPinnedColumn
          ? "2px 0 2px -2px rgb(203 213 225) inset"
          : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      opacity: 1,
      position: isPinned || isHeader ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isHeader && isPinned ? 30 : isHeader ? 20 : isPinned ? 1 : 0,
    };
  };

  return (
    <div className="rounded-md border">
      <div className={cn("w-full overflow-y-auto", className)}>
        <Table className="w-full border-separate border-spacing-0">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "sticky top-0 z-20 bg-white border-b border-slate-200",
                      headerStyling ?? "text-base font-bold select-none h-8 text-gray-800"
                    )}
                    style={{ ...getCommonPinningStyles(header.column, true) }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="dark:text-white">
            {loading ? (
              [...Array(displayAmount)].map((_, index) => (
                <TableRow key={`table-row-${index}`}>
                  {table.getAllColumns().map((col) => (
                    <TableCell
                      key={col.id}
                      className="text-sm border-b border-slate-200"
                      style={{ ...getCommonPinningStyles(col) }}
                    >
                      <ValueSkeleton className="max-h-[20px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isSelected = row.id === selectedRowId;

                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      disableRowColour
                        ? "bg-white"
                        : "odd:bg-gray-100 dark:odd:bg-gray-800 even:bg-white dark:even:bg-gray-900",
                      clickableRows && "cursor-pointer",
                      isSelected && "bg-slate-100"
                    )}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={clickableRows ? () => handleRowClick(row) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "text-sm border-b border-slate-200",
                          cell.column.getIsPinned() && "bg-inherit"
                        )}
                        style={{ ...getCommonPinningStyles(cell.column) }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t("nodataavailable")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


export const ActionButton = <TData, _TValue>({ dataObj, fn, text, ref, className, disabled }: { dataObj: TData, fn: (_obj: TData) => void, text?: string, ref?: any, className?: string, disabled?: boolean }) => {
  return (
    <Button
      ref={ref}
      size={"xs"}
      className={cn("dark:text-white", className)}
      variant="ghost"
      onClick={() => fn(dataObj)}
      disabled={disabled}
    >
      {text ?? "Details"}
    </Button>
  );
};

export const ActionIconButton = <TData, _TValue>({
  dataObj,
  fn,
  children,
  ref,
  className,
  tooltipText,
  variant,
  disabled
}: {
  dataObj: TData;
  fn: (_obj: TData) => void;
  children: React.ReactNode;
  ref?: any;
  className?: string;
  tooltipText?: string;
  variant?: "destructive";
  disabled?: boolean;
}) => {
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          className={cn("dark:text-white custom-button-icon", variant === "destructive" && "text-red-500 hover:text-red-500 hover:bg-red-500/10", className)}
          variant="ghost"
          onClick={() => fn(dataObj)}
          disabled={disabled}
        >
          {children}
        </Button>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export const SortingButton = <TData, TValue>({ column }: { column: Column<TData, TValue> }) => {
  return (
    <Button
      className={"ml-2 px-1 py-1"}
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    >
      <ArrowUpDown className="h-4 w-4" />
    </Button>
  );
};

export const TableColumnHeader = ({ text, column, hideSortingButton, align, icon, className }: { text: string, column: any, hideSortingButton?: boolean, align?: "start" | "end" | "center", icon?: JSX.Element, className?: string }) => {
  const isSortingEnabled = column.getCanSort();

  return (
    <div className={cn("flex items-center select-none", `justify-${align ?? "normal"}`, className)}>
      {icon} {text}
      {
        (hideSortingButton || !isSortingEnabled) ? null : <SortingButton column={column} />
      }
    </div>
  );
};

export const TableColumnHeaderWithHoverCard = ({ text, column, hoverCardText, hideSortingButton, align, icon, className }: { text: string, column: any, hoverCardText: string, hideSortingButton?: boolean, align?: "start" | "end" | "center", icon?: JSX.Element, className?: string }) => {
  const isSortingEnabled = column.getCanSort();

  return (
    <div className={cn("flex items-center select-none", `justify-${align ?? "normal"}`, className)}>
      <div className="flex items-center gap-2">
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div className="flex items-center gap-2">
              {text} {icon}
            </div>
          </HoverCardTrigger>
          <HoverCardContent side="right" className="w-80">
            {hoverCardText}
          </HoverCardContent>
        </HoverCard>
        {
          (hideSortingButton || !isSortingEnabled) ? null : <SortingButton column={column} />
        }
      </div>
    </div>
  );
};
